import { useEffect, useRef, useState } from "react";
import { Drawer, Modal, Tooltip, message } from "antd";
import { careService } from "../../services/careService";
import { preparePrescriptionFiles } from "./prescriptionImport";
import "./MedicationImportWorkspace.css";
import "./MedicationImportProgress.css";

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
const display = (value) => String(value || "").trim() || "Not found";

export default function MedicationImportWorkspace({ residentId, workspaceParams, actorId, uploadFile, onReviewMedication }) {
  const [activeImport, setActiveImport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preparingFiles, setPreparingFiles] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [drawerDocument, setDrawerDocument] = useState(null);
  const [localFileUrls, setLocalFileUrls] = useState({});
  const [previewDocument, setPreviewDocument] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setActiveImport(await careService.residentMedications.getActiveImport(residentId, workspaceParams)); }
    catch (error) { message.error(errorMessage(error, "Unable to load the current medication import.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [residentId, workspaceParams?.workspaceId, workspaceParams?.wid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const urls = Object.fromEntries(files.map((file, index) => [`${file.name}-${file.size}-${index}`, URL.createObjectURL(file)]));
    setLocalFileUrls(urls);
    return () => Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const selectFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    setPreparingFiles(true);
    try {
      const prepared = await preparePrescriptionFiles(selected);
      setFiles((current) => {
        const combined = [...current];
        prepared.forEach((file) => {
          const duplicate = combined.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified);
          if (!duplicate) combined.push(file);
        });
        if (combined.length > 10) {
          message.warning("You can import up to 10 prescription files at a time.");
          return combined.slice(0, 10);
        }
        return combined;
      });
    } catch (error) {
      message.error(error?.message || "Unable to prepare the selected prescription file.");
    } finally {
      setPreparingFiles(false);
    }
  };

  const openFileBrowser = () => {
    if (!files.length) { inputRef.current?.click(); return; }
    Modal.confirm({
      title: "Add files to this prescription?",
      content: "Only add files that belong to the same prescription already selected. Do not combine files from different prescriptions.",
      okText: "Add files",
      cancelText: "Cancel",
      onOk: () => inputRef.current?.click(),
    });
  };

  const startImport = async () => {
    if (!residentId) { message.warning("Select a resident before importing prescriptions."); return; }
    if (!files.length || busy) return;
    setBusy(true);
    try {
      const fileIds = [];
      for (const file of files) {
        const result = await uploadFile(null, file, {
          confirmUpload: false, fileType: file.type || "DOCUMENT", includeUserId: false,
          preserveOriginal: true, purpose: "OTHER", skipUserValidation: true,
        });
        const fileId = result?.fileId || result?.file?.id;
        if (!fileId) throw new Error(`Upload failed for ${file.name}.`);
        fileIds.push(fileId);
      }
      const created = await careService.residentMedications.createImport(residentId, fileIds, workspaceParams, actorId);
      setActiveImport(created); setFiles([]); setDrawerDocument(created.sourceDocuments?.[0] || null); setPreviewOpen(true);
      message.success("Prescription data extracted. Confirm the resident before reviewing medications.");
    } catch (error) { message.error(errorMessage(error, "Unable to import the prescription files.")); }
    finally { setBusy(false); }
  };

  const confirmResident = async () => {
    if (!confirmed) { message.warning("Confirm that the prescription belongs to this resident."); return; }
    setBusy(true);
    try {
      const updated = await careService.residentMedications.confirmImportResident(
        activeImport.id, { confirmed: true }, workspaceParams, actorId);
      setActiveImport(updated); message.success("Resident confirmed. Medication review is now available.");
    } catch (error) { message.error(errorMessage(error, "Unable to confirm the resident.")); }
    finally { setBusy(false); }
  };

  const reviewMedication = async (index) => {
    const item = activeImport?.importPayload?.medications?.[index];
    if (!item) {
      message.error("The imported medication is no longer available.");
      return;
    }
    setBusy(true);
    try {
      let reviewRecord = null;
      if (item.newMedicationId) {
        const records = await careService.residentMedications.listNewMedications(residentId, {
          ...workspaceParams,
          status: "ALL",
        });
        reviewRecord = (Array.isArray(records) ? records : records?.content || [])
          .find((record) => String(record.id) === String(item.newMedicationId));
      }
      const medicationReviewRecord = reviewRecord || {
        ...item,
        residentId,
        sourceType: "Imported prescription",
        status: "PENDING_OLIVEEB_APPROVAL",
        prescriptionDocument: activeImport.sourceDocuments || [],
        extractedMedicationDetails: {
          ...item,
          medicationImportId: activeImport.id,
          importMedicationIndex: index,
        },
      };
      setPreviewOpen(false);
      onReviewMedication(medicationReviewRecord, { importId: activeImport.id, index });
    } catch (error) {
      message.error(errorMessage(error, "Unable to open the imported medication for review."));
    } finally {
      setBusy(false);
    }
  };

  const complete = () => Modal.confirm({
    title: "Complete medication import?",
    content: "Complete this import after every extracted medication has been reviewed. Medications waiting for approval will keep that status and can be approved separately.",
    okText: "Complete import",
    onOk: async () => {
      try {
        const updated = await careService.residentMedications.completeImport(activeImport.id, workspaceParams, actorId);
        setActiveImport(null); setConfirmed(false); setPreviewOpen(false);
        message.success(`Medication import for ${updated.residentName} completed.`);
      } catch (error) {
        message.error(errorMessage(error, "Unable to complete the medication import."));
      }
    },
  });

  const cancelImport = () => Modal.confirm({
    title: "Cancel medication import?",
    content: "This import will be closed and you will be able to start a new prescription import.",
    okText: "Cancel import",
    okButtonProps: { danger: true },
    cancelText: "Keep import",
    onOk: async () => {
      try {
        await careService.residentMedications.cancelImport(activeImport.id, workspaceParams, actorId);
        setActiveImport(null); setConfirmed(false); setPreviewOpen(false); setDrawerDocument(null);
        message.success("Medication import cancelled.");
      } catch (error) {
        message.error(errorMessage(error, "Unable to cancel the medication import."));
      }
    },
  });

  const openImportPreview = () => {
    setDrawerDocument((current) => current || activeImport?.sourceDocuments?.[0] || null);
    setPreviewOpen(true);
  };

  if (loading) return <div className="med-import-loading"><i className="ti ti-loader-2" /> Loading medication import...</div>;
  const comparison = activeImport?.importPayload?.residentComparison || {};
  const medications = activeImport?.importPayload?.medications || [];
  const approvedCount = medications.filter((item) => item.reviewStatus === "APPROVED").length;
  const waitingApprovalCount = medications.filter((item) => item.reviewStatus === "WAITING_APPROVAL").length;
  const cancellationLocked = medications.some((item) =>
    ["APPROVED", "WAITING_APPROVAL"].includes(String(item.reviewStatus || "").toUpperCase()));
  const completionEligibleCount = approvedCount + waitingApprovalCount;
  const allMedicationsReady = Boolean(medications.length) && completionEligibleCount === medications.length;
  const documentUrls = Object.fromEntries((activeImport?.sourceDocuments || []).map((document) => [
    document.fileId || document.storageFileId,
    document.signedUrl || "",
  ]));

  return (
    <div className="med-import-workspace">
      <MedicationImportProgress
        activeImport={activeImport}
        reviewedCount={completionEligibleCount}
        medicationCount={medications.length}
      />
      {activeImport ? (
        <MedicationImportDocuments
          documents={activeImport.sourceDocuments}
          documentUrls={documentUrls}
          onPreview={setPreviewDocument}
        />
      ) : null}
      {!activeImport ? (
        <section className="med-import-empty">
          <span className="round-icon blue"><i className="ti ti-file-import" /></span>
          <div><h3>Import prescriptions</h3><p>Upload prescription files, verify resident details, then review every extracted medication before approval.</p></div>
          <input ref={inputRef} hidden multiple type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.jpe,.jfif,.webp,.heic,.heif,image/heic,image/heif" onChange={selectFiles} />
          <button className="mini-btn" type="button" onClick={openFileBrowser} disabled={!residentId || busy || preparingFiles || files.length >= 10}><i className={`ti ${preparingFiles ? "ti-loader-2" : "ti-paperclip"}`} /> {preparingFiles ? "Converting HEIC..." : files.length ? "Add files" : "Browse files"}</button>
          {files.length ? <p className="med-import-file-warning"><i className="ti ti-alert-triangle" /><span><strong>Keep one prescription per import.</strong> All selected files must belong to the same prescription. Do not add files from a different prescription.</span></p> : null}
          {files.length ? (
            <MedicationImportDocuments
              documents={files.map((file, index) => ({
                fileName: file.name,
                contentType: file.type,
                localPreviewUrl: localFileUrls[`${file.name}-${file.size}-${index}`],
              }))}
              documentUrls={{}}
              label="SELECTED FILES"
              onPreview={setPreviewDocument}
              onRemove={(index) => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
            />
          ) : null}
          <button className="mini-btn success" type="button" onClick={startImport} disabled={!files.length || busy || preparingFiles}>{busy ? "Extracting..." : "Import and preview"}</button>
        </section>
      ) : (
        <>
          <div className="med-import-toolbar">
            <div><span>ACTIVE IMPORT</span><h3>{activeImport.residentName}</h3><p>{completionEligibleCount} of {medications.length} medications reviewed{waitingApprovalCount ? ` · ${waitingApprovalCount} waiting approval` : ""}</p></div>
            <button className="mini-btn med-import-view-button" type="button" onClick={openImportPreview}><i className="ti ti-eye" /> View import</button>
            <button className="mini-btn success" type="button" disabled={!allMedicationsReady} onClick={complete}><i className="ti ti-check" /> Mark completed</button>
            <Tooltip
              title={cancellationLocked
                ? "This import can no longer be cancelled because at least one medication has been approved or sent for approval."
                : ""}
            >
              <span className="med-import-cancel-button-wrap">
                <button
                  className="mini-btn med-import-cancel-button"
                  type="button"
                  disabled={busy || cancellationLocked}
                  onClick={cancelImport}
                >
                  <i className="ti ti-x" /> Cancel import
                </button>
              </span>
            </Tooltip>
          </div>
          <p className="med-import-lock-note"><i className="ti ti-lock" /> Complete this resident's import before starting another for the same resident.</p>
          <MedicationImportDetails value={activeImport} comparison={comparison} medications={medications} confirmed={confirmed} busy={busy} setConfirmed={setConfirmed} onConfirm={confirmResident} onReview={reviewMedication} />
        </>
      )}
      <Drawer
        title="Medication import preview"
        width="100vw"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        className="resident-action-drawer med-import-drawer medication-document-preview-drawer"
        rootClassName="med-import-fullscreen-drawer"
      >
        {activeImport ? <div className="med-import-drawer-layout">
          <MedicationImportDetails value={activeImport} comparison={comparison} medications={medications} confirmed={confirmed} busy={busy} setConfirmed={setConfirmed} onConfirm={confirmResident} onReview={reviewMedication} />
          <ImportedPrescriptionPane documents={activeImport.sourceDocuments} documentUrls={documentUrls} selected={drawerDocument} onSelect={setDrawerDocument} />
        </div> : null}
      </Drawer>
      <Modal className="med-import-document-modal" footer={null} open={Boolean(previewDocument)} onCancel={() => setPreviewDocument(null)} title={previewDocument?.fileName || previewDocument?.originalFileName || "Prescription preview"} width={960}>
        {previewDocument ? <DocumentPreview document={previewDocument} url={previewDocument.localPreviewUrl || documentUrls[previewDocument.fileId || previewDocument.storageFileId]} /> : null}
      </Modal>
    </div>
  );
}

function MedicationImportDocuments({ documents, documentUrls, onPreview, onRemove, label = "IMPORTED FILES" }) {
  const items = Array.isArray(documents) ? documents : [];
  if (!items.length) return null;
  return (
    <section className="med-import-documents">
      <div><span>{label}</span><strong>{items.length} prescription file{items.length === 1 ? "" : "s"}</strong></div>
      <div className="med-import-document-strip">
        {items.map((document, index) => {
          const fileId = document.fileId || document.storageFileId;
          const url = document.localPreviewUrl || documentUrls[fileId];
          const image = String(document.contentType || "").startsWith("image/");
          return <article key={fileId || `${document.fileName}-${index}`}>
            <button className="med-import-document-preview" type="button" disabled={!url} onClick={() => onPreview(document)} title={url ? "Preview prescription" : "Preview is loading"}>
              <span>{image && url ? <img src={url} alt="" /> : <i className={`ti ${url ? "ti-file-type-pdf" : "ti-loader-2"}`} />}</span>
              <small>{document.fileName || document.originalFileName || `Prescription ${index + 1}`}</small>
              {url ? <i className="ti ti-eye" /> : null}
            </button>
            {onRemove ? <button aria-label={`Remove ${document.fileName || "file"}`} className="med-import-document-remove" type="button" onClick={() => onRemove(index)} title="Remove file"><i className="ti ti-x" /></button> : null}
          </article>;
        })}
      </div>
    </section>
  );
}

function ImportedPrescriptionPane({ documents, documentUrls, selected, onSelect }) {
  const items = Array.isArray(documents) ? documents : [];
  const active = selected || items[0] || null;
  const activeId = active?.fileId || active?.storageFileId;
  const activeUrl = active?.localPreviewUrl || documentUrls[activeId];
  return (
    <section className="assessment-drawer-section medication-documents-section new-medication-prescription-pane med-import-prescription-pane">
      <h3>Prescription Document</h3>
      <div className="new-medication-document-live-preview">
        {active ? <DocumentPreview document={active} url={activeUrl} /> : <div className="new-medication-document-preview-empty"><i className="ti ti-file-description" /><strong>No prescription document</strong><small>Select a document to preview it.</small></div>}
      </div>
      {items.length > 1 ? <div className="new-medication-document-thumbnail-strip" aria-label="Prescription document thumbnails">
        {items.map((document, index) => {
          const fileId = document.fileId || document.storageFileId;
          const url = document.localPreviewUrl || documentUrls[fileId];
          const selectedId = active?.fileId || active?.storageFileId;
          const isSelected = fileId ? fileId === selectedId : document === active;
          const image = String(document.contentType || "").startsWith("image/");
          return <button aria-label={`Show ${document.fileName || `prescription ${index + 1}`} preview`} className={`new-medication-document-preview-thumb ${isSelected ? "is-selected" : ""}`} disabled={!url} key={fileId || `${document.fileName}-${index}`} onClick={() => onSelect(document)} title={document.fileName || `Prescription ${index + 1}`} type="button">
            {image && url ? <img src={url} alt="" /> : <i className="ti ti-file-description" />}
            <span>{index + 1}</span>
          </button>;
        })}
      </div> : null}
      <small className="med-import-active-document-name">{active?.fileName || active?.originalFileName || "Prescription document"}</small>
    </section>
  );
}

function DocumentPreview({ document, url }) {
  const [rotation, setRotation] = useState(0);
  useEffect(() => setRotation(0), [url]);

  if (!url) return <div className="med-import-preview-unavailable">Preview unavailable.</div>;
  if (String(document.contentType || "").startsWith("image/")) return (
    <div className="med-import-image-preview">
      <div className="med-import-image-preview-controls" aria-label="Rotate prescription image">
        <button type="button" onClick={() => setRotation((value) => value - 90)} aria-label="Rotate image left" title="Rotate left"><i className="ti ti-rotate-2" /></button>
        <button type="button" onClick={() => setRotation((value) => value + 90)} aria-label="Rotate image right" title="Rotate right"><i className="ti ti-rotate-clockwise-2" /></button>
      </div>
      <div className="med-import-image-preview-canvas">
        <img className="med-import-preview-image" style={{ transform: `rotate(${rotation}deg)` }} src={url} alt={document.fileName || "Prescription"} />
      </div>
    </div>
  );
  const isPdf = String(document.contentType || "").includes("pdf") || String(document.fileName || document.originalFileName || "").toLowerCase().endsWith(".pdf");
  if (isPdf) return <iframe className="med-import-preview-frame" src={url} title={document.fileName || "Prescription"} />;
  return <div className="new-medication-document-preview-empty"><i className="ti ti-file-description" /><strong>{document.fileName || "Prescription document"}</strong><small>This file type cannot be previewed here.</small></div>;
}

function MedicationImportProgress({ activeImport, reviewedCount, medicationCount }) {
  const residentConfirmed = Boolean(activeImport?.residentConfirmed);
  const reviewComplete = Boolean(medicationCount) && reviewedCount === medicationCount;
  const steps = [
    { key: "upload", label: "Upload Prescription Document", detail: activeImport ? "Files imported" : "Add prescriptions", complete: Boolean(activeImport), current: !activeImport },
    { key: "resident", label: "Verify resident", detail: residentConfirmed ? "Confirmed" : "Check identity", complete: residentConfirmed, current: Boolean(activeImport) && !residentConfirmed },
    { key: "review", label: "Review medications", detail: medicationCount ? `${reviewedCount} of ${medicationCount} reviewed` : "Review each item", complete: reviewComplete, current: residentConfirmed && !reviewComplete },
    { key: "complete", label: "Complete", detail: reviewComplete ? "Ready to complete" : "Finish import", complete: false, current: reviewComplete },
  ];

  return (
    <section className="med-import-progress" aria-label="Medication import progress">
      <div className="med-import-progress-heading">
        <div><span>IMPORT WORKFLOW</span><h3>Medication import progress</h3></div>
        {activeImport ? <small>Complete each step before starting another import.</small> : null}
      </div>
      <ol>
        {steps.map((step, index) => (
          <li className={`${step.complete ? "is-complete" : ""} ${step.current ? "is-current" : ""}`} key={step.key} aria-current={step.current ? "step" : undefined}>
            <span className="med-import-step-number">{step.complete ? <i className="ti ti-check" /> : index + 1}</span>
            <div><strong>{step.label}</strong><small>{step.detail}</small></div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function MedicationImportDetails({ value, comparison, medications, confirmed, busy, setConfirmed, onConfirm, onReview }) {
  return <div className="med-import-details">
    <section className="med-import-section">
      <div className="med-import-section-title"><div><h3>Resident verification</h3><p>Compare extracted prescription details with the selected resident.</p></div>{value.hasResidentDiscrepancy ? <em className="pill medium">Discrepancy</em> : <em className="pill low">Match</em>}</div>
      <div className="med-import-comparison">
        <div><small>Field</small><strong>Name</strong><strong>Date of birth</strong></div>
        <div><small>Extracted</small><span>{display(comparison.extractedName)}</span><span>{display(comparison.extractedDateOfBirth)}</span></div>
        <div><small>Resident record</small><span>{display(comparison.actualName)}</span><span>{display(comparison.actualDateOfBirth)}</span></div>
        <div><small>Result</small><span className={comparison.nameMatches ? "match" : "warning"}>{comparison.nameMatches ? "Matches" : "Review"}</span><span className={comparison.dateOfBirthMatches ? "match" : "warning"}>{comparison.dateOfBirthMatches ? "Matches" : "Review"}</span></div>
      </div>
      {value.importPayload?.extractionWarnings ? <div className="med-import-warning"><strong>Extraction warning</strong><p>{value.importPayload.extractionWarnings}</p></div> : null}
      {!value.residentConfirmed ? <div className="med-import-confirm">
        <div className="med-import-resident-safety-warning"><i className="ti ti-alert-triangle" /><span><strong>Verify the prescription before continuing.</strong> Check the attached prescription and confirm that every medication belongs to {value.residentName}. Do not continue if the prescription is for a different resident.</span></div>
        <label><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> I confirm these medications belong to resident <strong className="med-import-confirm-resident">{value.residentName}</strong>. Any resident detail mismatch is an extraction error only.</label>
        <button className="mini-btn success" type="button" disabled={busy || !confirmed} onClick={onConfirm}>Confirm resident</button>
      </div> : <p className="med-import-confirmed"><i className="ti ti-circle-check" /> Resident confirmed</p>}
    </section>
    <section className="med-import-section"><div className="med-import-section-title"><div><h3>Extracted medications</h3><p>Review each medication against the attached prescription, then approve it or send it for approval.</p></div><strong>{medications.length}</strong></div><div className="med-import-medications">{medications.map((item, index) => { const reviewFinished = ["APPROVED", "WAITING_APPROVAL"].includes(item.reviewStatus); return <article key={`${item.medicationName}-${index}`}><div><span>{index + 1}</span><div><h4>{display(item.medicationName)}</h4><p>{[item.dosage, item.doseUnit, item.route, item.frequency].filter(Boolean).join(" · ") || "Details require review"}</p></div></div><em className={`pill ${reviewFinished ? "low" : "medium"}`}>{item.reviewStatus === "APPROVED" ? "Approved" : item.reviewStatus === "WAITING_APPROVAL" ? "Waiting approval" : item.reviewStatus === "DRAFT_CREATED" ? "In review" : "Pending"}</em>{value.residentConfirmed ? <button className="mini-btn med-import-review-button" type="button" disabled={busy || reviewFinished} onClick={() => onReview(index)}><i className="ti ti-edit" /> {item.reviewStatus === "DRAFT_CREATED" ? "Continue review" : "Review medication"}</button> : null}</article>; })}</div></section>
  </div>;
}
